import React, { useState, useEffect } from 'react';
import { adminApi } from '../../../services/api';
import { Spinner, KpiCard } from './shared';
import { MousePointerClick, Users, Percent, Star, ExternalLink } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const PLATFORM_CONFIG = {
  google: { label: 'Google', color: '#4285F4' },
  tripadvisor: { label: 'TripAdvisor', color: '#00AF87' },
  yelp: { label: 'Yelp', color: '#D32323' },
  facebook: { label: 'Facebook', color: '#1877F2' },
  trustpilot: { label: 'Trustpilot', color: '#00B67A' },
  unknown: { label: 'Altro', color: '#94A3B8' },
};

export function ReviewsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.get('/admin/analytics/reviews')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  if (!data) return <p className="text-[#64748B] text-center py-8">Errore nel caricamento dati</p>;

  const { kpi, by_platform, daily_trend, recent_clicks } = data;
  const hasData = kpi.total_clicks > 0;

  return (
    <div className="space-y-6" data-testid="reviews-analytics-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Click totali" value={kpi.total_clicks} icon={MousePointerClick} color="#D44E20" testid="review-kpi-total-clicks" />
        <KpiCard label="Clienti convertiti" value={kpi.unique_clickers} icon={Users} color="#3B82F6" testid="review-kpi-unique" />
        <KpiCard label="Tasso conversione" value={`${kpi.conversion_rate}%`} sub={`su ${kpi.total_customers} clienti`} icon={Percent} color="#10B981" testid="review-kpi-conversion" />
        <KpiCard label="Punti bonus distribuiti" value={kpi.total_bonus_points} icon={Star} color="#F59E0B" testid="review-kpi-bonus" />
      </div>

      {!hasData && (
        <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <MousePointerClick size={40} className="text-slate-200 mx-auto mb-3" />
          <p className="text-[#1A1A1A] font-medium">Nessun click registrato</p>
          <p className="text-sm text-[#64748B] mt-1">Configura i link recensioni in Contatti per iniziare a tracciare i click.</p>
        </div>
      )}

      {hasData && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* By platform */}
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="review-by-platform">
              <h3 className="text-lg font-semibold text-[#1A1A1A] tracking-tight mb-4">Click per piattaforma</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={by_platform} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#64748B' }} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="platform"
                    tick={{ fontSize: 12, fill: '#1A1A1A' }}
                    tickFormatter={v => (PLATFORM_CONFIG[v] || PLATFORM_CONFIG.unknown).label}
                    width={90}
                  />
                  <Tooltip
                    contentStyle={{ borderRadius: 16, fontSize: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #E2E8F0' }}
                    formatter={(val, name) => [val, name === 'total_clicks' ? 'Click' : name === 'unique_customers' ? 'Clienti unici' : name]}
                  />
                  <Bar dataKey="total_clicks" name="Click" radius={[0, 8, 8, 0]} fill="#D44E20" />
                  <Bar dataKey="unique_customers" name="Clienti unici" radius={[0, 8, 8, 0]} fill="#3B82F6" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Platform breakdown cards */}
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="review-platform-detail">
              <h3 className="text-lg font-semibold text-[#1A1A1A] tracking-tight mb-4">Dettaglio piattaforme</h3>
              <div className="space-y-3">
                {by_platform.map(p => {
                  const cfg = PLATFORM_CONFIG[p.platform] || PLATFORM_CONFIG.unknown;
                  const convRate = kpi.total_customers > 0 ? ((p.unique_customers / kpi.total_customers) * 100).toFixed(1) : 0;
                  return (
                    <div key={p.platform} className="flex items-center gap-4 p-3 rounded-xl border border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`review-platform-${p.platform}`}>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${cfg.color}15` }}>
                        <ExternalLink size={18} style={{ color: cfg.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1A1A1A]">{cfg.label}</p>
                        <p className="text-xs text-[#64748B]">{p.unique_customers} clienti unici &middot; {convRate}% conversione</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-[#1A1A1A] tabular-nums">{p.total_clicks}</p>
                        <p className="text-[10px] text-[#94A3B8]">click</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Daily trend */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="review-daily-trend">
            <h3 className="text-lg font-semibold text-[#1A1A1A] tracking-tight mb-4">Trend click ultimi 30 giorni</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={daily_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748B' }} interval={4} />
                <YAxis tick={{ fontSize: 11, fill: '#64748B' }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 16, fontSize: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #E2E8F0' }} />
                <Line type="monotone" dataKey="total" stroke="#D44E20" strokeWidth={2.5} dot={{ r: 3, fill: '#D44E20', strokeWidth: 0 }} name="Click totali" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Recent clicks */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="review-recent-clicks">
            <h3 className="text-lg font-semibold text-[#1A1A1A] tracking-tight mb-4">Ultimi click</h3>
            <div className="divide-y divide-slate-100">
              {recent_clicks.map((c, i) => {
                const cfg = PLATFORM_CONFIG[c.platform] || PLATFORM_CONFIG.unknown;
                return (
                  <div key={i} className="flex items-center justify-between py-3 px-2 hover:bg-slate-50 rounded-xl transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${cfg.color}15` }}>
                        <ExternalLink size={14} style={{ color: cfg.color }} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#1A1A1A]">{c.customer_name}</p>
                        <p className="text-xs text-[#64748B]">{cfg.label}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {c.bonus_awarded && (
                        <p className="text-xs font-medium text-[#F59E0B]">+{c.points} pt</p>
                      )}
                      <p className="text-[10px] text-[#94A3B8]">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                      </p>
                    </div>
                  </div>
                );
              })}
              {recent_clicks.length === 0 && <p className="text-[#64748B] text-sm text-center py-4">Nessun click recente</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
